import {
  XMarkIcon,
  DocumentTextIcon,
  ClockIcon,
  UserIcon,
  TagIcon,
  CheckIcon,
  PlusIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';

import { contextManager } from '../../services/contextManager';
import { projectService } from '../../services/projectService';
import { logger } from '../../utils/logger';
import { MarkdownText } from '../chat/MarkdownText';
import { ExistingDocument } from '../ContextBrowser/ContextBrowser';
import { DocumentChunk } from '../ContextBrowser/DocumentChunk';

interface DocumentPreviewProps {
  document: ExistingDocument | null;
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (_documentId: string) => void;
  onDeselect?: (_documentId: string) => void;
  isSelected?: boolean;
  isLoading?: boolean;
}

interface DocumentContent {
  content: string;
  pageCount?: number | null;
  extractedText?: string;
  metadata?: {
    title?: string;
    author?: string;
    createdDate?: Date;
    keywords?: string[];
  };
}

const documentTypeColors = {
  'BRD': 'bg-blue-100 text-blue-800',
  'ADR': 'bg-purple-100 text-purple-800',
  'Standard': 'bg-green-100 text-green-800',
  'Reference Architecture': 'bg-orange-100 text-orange-800',
  'Legacy Documentation': 'bg-gray-100 text-gray-800',
  'User Docs': 'bg-pink-100 text-pink-800',
  'Other': 'bg-gray-100 text-gray-800',
};

// Helper to map document.type to display label
const getDocumentTypeLabel = (type: string): string => {
  if (type === 'user_docs') {return 'User Docs';}
  if (type === 'guidelines') {return 'Guidelines';}
  return type;
};

/**
 * DocumentPreview - Modal component for previewing documents
 * Shows document content, metadata, and provides selection/download actions
 */
export const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  document,
  isOpen,
  onClose,
  onSelect,
  onDeselect,
  isSelected = false,
  isLoading = false,
}) => {
  const [documentContent, setDocumentContent] = useState<DocumentContent | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'preview' | 'metadata'>('preview');

  // Simulate loading document content
  useEffect(() => {
    if (document && isOpen) {
      setContentLoading(true);
      setLoadError(null);
      setDocumentContent(null);
      
      // Check if this is an uploaded document with blob path
      if (document.blobPath) {
        // Load real content from API
        loadRealDocumentContent(document);
      } else {
        // Knowledge base documents (wiki, guidelines, user_docs) - load from chunks
        loadKnowledgeBaseContent(document);
      }
    }
  }, [document, isOpen]);

  const loadRealDocumentContent = async (doc: ExistingDocument) => {
    try {
      // Get project ID from context manager
      const projectId = contextManager.getContextData().project_id;

      if (!projectId || !doc.blobPath) {
        throw new Error('Missing project ID or blob path');
      }

      // Call the API to get document content
      const response = await projectService.getUploadedDocumentContent(
        Number(projectId),
        doc.blobPath,
      );

      setDocumentContent({
        content: response.content,
        pageCount: null, // Not available from API
        extractedText: response.content,
        metadata: {
          title: response.filename,
          author: doc.author,
          createdDate: response.updated_at
            ? new Date(response.updated_at)
            : doc.lastModified,
          keywords: doc.tags,
        },
      });
      setLoadError(null);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      logger.error(
        'Failed to load document content from API:',
        errorMessage,
      );
      setLoadError(
        `Unable to load document content. ${errorMessage}. ` +
        'The document may still be processing or there may be a ' +
        'temporary connectivity issue.',
      );
      setDocumentContent(null);
    } finally {
      setContentLoading(false);
    }
  };

  const loadKnowledgeBaseContent = async (doc: ExistingDocument) => {
    try {
      // For knowledge base documents, use chunk data that's already loaded
      if (doc.chunks && doc.chunks.length > 0) {
        // Combine all chunks to create a complete preview
        const sortedChunks = [...doc.chunks].sort(
          (a: DocumentChunk, b: DocumentChunk) => a.chunk_order - b.chunk_order,
        );
        const combinedContent = sortedChunks
          .map((chunk: DocumentChunk) => chunk.chunked_content)
          .join('\n\n---\n\n');

        setDocumentContent({
          content: combinedContent,
          pageCount: null,
          extractedText: combinedContent,
          metadata: {
            title: doc.name,
            author: doc.author,
            createdDate: doc.lastModified,
            keywords: doc.tags,
          },
        });
        setLoadError(null);
      } else if (doc.snippet) {
        // Fallback to snippet if chunks are not available
        const snippetContent = doc.snippet;
        setDocumentContent({
          content: snippetContent,
          pageCount: null,
          extractedText: snippetContent,
          metadata: {
            title: doc.name,
            author: doc.author,
            createdDate: doc.lastModified,
            keywords: doc.tags,
          },
        });
        setLoadError(null);
      } else if (doc.summary) {
        // Last resort fallback to summary
        const summaryContent = doc.summary;
        setDocumentContent({
          content: summaryContent,
          pageCount: null,
          extractedText: summaryContent,
          metadata: {
            title: doc.name,
            author: doc.author,
            createdDate: doc.lastModified,
            keywords: doc.tags,
          },
        });
        setLoadError(null);
      } else {
        setLoadError(
          'No preview content available for this document. ' +
          'You can still add this document to your context.',
        );
        setDocumentContent(null);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      logger.error(
        'Failed to load knowledge base content:',
        errorMessage,
      );
      setLoadError(
        'Unable to load knowledge base document. ' +
        'Please try again later.',
      );
      setDocumentContent(null);
    } finally {
      setContentLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) {return '0 Bytes';}
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const handleToggleSelection = () => {
    if (!document) {return;}
    
    if (isSelected && onDeselect) {
      onDeselect(document.id);
    } else if (!isSelected && onSelect) {
      onSelect(document.id);
    }
  };

  if (!isOpen || !document) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <button 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
          aria-label="Close preview"
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <DocumentTextIcon className="w-6 h-6 text-gray-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-gray-900 truncate">
                  {document.name}
                </h2>
              </div>
            </div>

            {/* Header Actions */}
            <div className="flex items-center gap-2 ml-4">
              {(onSelect || onDeselect) && (
                <button
                  onClick={handleToggleSelection}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isSelected
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-[#0097a9] text-white hover:bg-[#007a8a]'
                  }`}
                >
                  {isSelected ? (
                    <>
                      <CheckIcon className="w-4 h-4" />
                      Selected
                    </>
                  ) : (
                    <>
                      <PlusIcon className="w-4 h-4" />
                      Add to Context
                    </>
                  )}
                </button>
              )}

              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex px-6">
              <button
                onClick={() => setActiveTab('preview')}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'preview'
                    ? 'border-[#0097a9] text-[#0097a9]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Preview
              </button>
              {!document.blobPath && (
                <button
                  onClick={() => setActiveTab('metadata')}
                  className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'metadata'
                      ? 'border-[#0097a9] text-[#0097a9]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Metadata
                </button>
              )}
            </nav>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-96">
            {isLoading || contentLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0097a9]"></div>
                <span className="ml-2 text-gray-600">Loading document...</span>
              </div>
            ) : loadError ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md w-full">
                  <div className="flex items-start gap-3">
                    <ExclamationTriangleIcon 
                      className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5"
                    />
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-red-900 mb-2">
                        Preview Unavailable
                      </h3>
                      <p className="text-sm text-red-800">
                        {loadError}
                      </p>
                      <div className="mt-4 text-xs text-red-700">
                        You can still view the metadata tab or add this document to your context.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {activeTab === 'preview' && documentContent && (
                  <div className="space-y-4">
                    <div className="prose max-w-none">
                      <div className="bg-white border rounded-lg p-4">
                        <MarkdownText 
                          content={documentContent.content} 
                          className="text-sm leading-relaxed text-gray-800"
                        />
                      </div>
                    </div>
                    
                    {documentContent.pageCount && (
                      <div className="text-sm text-gray-500 border-t pt-4">
                        Document contains {documentContent.pageCount} page{documentContent.pageCount !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'metadata' && (
                  <div className="space-y-6">
                    {/* Basic Info */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 mb-3">Document Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                            File Name
                          </span>
                          <p className="mt-1 text-sm text-gray-900">{document.name}</p>
                        </div>
                        <div>
                          <span className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                            File Size
                          </span>
                          <p className="mt-1 text-sm text-gray-900">{formatFileSize(document.size)}</p>
                        </div>
                        <div>
                          <span className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Category
                          </span>
                          <p className="mt-1 text-sm text-gray-900">{document.category}</p>
                        </div>
                        <div>
                          <span className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Document Type
                          </span>
                          <span className={`inline-block mt-1 px-2 py-1 text-xs font-medium rounded ${
                            documentTypeColors[getDocumentTypeLabel(document.type) as keyof typeof documentTypeColors] || documentTypeColors['Other']
                          }`}>
                            {getDocumentTypeLabel(document.type)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Author and Dates */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 mb-3">Author & Dates</h3>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <UserIcon className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-900">{document.author}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ClockIcon className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            Last modified: {formatDate(document.lastModified)}
                          </span>
                        </div>
                        {documentContent?.metadata?.createdDate && (
                          <div className="flex items-center gap-2">
                            <ClockIcon className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              Created: {formatDate(documentContent.metadata.createdDate)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Tags */}
                    {document.tags.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 mb-3">Tags</h3>
                        <div className="flex items-center gap-2">
                          <TagIcon className="w-4 h-4 text-gray-400" />
                          <div className="flex flex-wrap gap-2">
                            {document.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Description */}
                    {document.description && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 mb-3">Description</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {document.description}
                        </p>
                      </div>
                    )}

                    {/* File Path */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 mb-3">File Location</h3>
                      <p className="text-sm text-gray-600 font-mono bg-gray-50 px-3 py-2 rounded">
                        {document.path}
                      </p>
                    </div>

                    {/* Wiki Link - for wiki documents */}
                    {document.source === 'wiki' && document.wiki_path && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 mb-3">Wiki Link</h3>
                        <a
                          href={document.wiki_path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 hover:underline break-all"
                        >
                          {document.wiki_path}
                        </a>
                      </div>
                    )}

                    {/* Content Stats */}
                    {documentContent && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 mb-3">Content Statistics</h3>
                        <div className="grid grid-cols-2 gap-4">
                          {documentContent.pageCount && (
                            <div>
                              <span className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Pages
                              </span>
                              <p className="mt-1 text-sm text-gray-900">{documentContent.pageCount}</p>
                            </div>
                          )}
                          <div>
                            <span className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Characters
                            </span>
                            <p className="mt-1 text-sm text-gray-900">
                              {documentContent.content.length.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
